export type ClientErrorParams = Record<string, unknown>;

/**
 * Ошибки, которые пробрасываются на клиент для отображения вместо Internal Error.
 */
export class ClientError extends Error {
	/**
	 * @param code Код ошибки
	 * @param params Опциональные параметры ошибки, которые будут переданы на клиент
	 */
	constructor(
		private readonly code: ClientErrorCode,
		private readonly params: ClientErrorParams | null = null,
	) {
		super(`ClientError#${code}`);

		const rawMessage = errorCodeToMessage[code];

		this.name = 'ClientError';
		this.message = params
			? Object.entries(params).reduce((msg, [key, value]) => msg.replace(`{${key}}`, String(value)), rawMessage)
			: rawMessage;
	}

	public getCode(): ClientErrorCode {
		return this.code;
	}

	public getParams(): ClientErrorParams | null {
		return this.params;
	}
}

export const enum ClientErrorCode {
	InternalError = 'internal_error',
	HousesEditorError = 'houses_editor_error',
	NotAuthorized = 'not_authorized',
	AccountNotFound = 'account_not_found',
	WrongPassword = 'wrong_password',
	EmailTaken = 'email_taken',
	LoginTaken = 'login_taken',
	AlreadyAuthorized = 'already_authorized',
	WrongServerPassword = 'wrong_server_password',
	WrongClosedBetaCode = 'wrong_closed_beta_code',
	NotEnoughMoney = 'not_enough_money',
	VehicleNotFound = 'vehicle_not_found',
	VehicleSeatTaken = 'vehicle_seat_taken',
	VehicleDoorsClosed = 'vehicle_doors_closed',
	MaxSpawnedVehiclesReached = 'max_spawned_vehicles_reached',
	NumberplateRegionNotFound = 'numberplate_region_not_found',
	AccessDenied = 'access_denied',
	FuelTypeNotCompatible = 'fuel_type_not_compatible',
	BankExchangeDirectionNotAllowed = 'bank_exchange_direction_not_allowed',
	BankTransferSelf = 'bank_transfer_self',
	PlayerNotFound = 'player_not_found',
	DataFetchingFailed = 'data_fetching_failed',
	PlayerOnJob = 'player_on_job',
	PlayerInDimension = 'player_in_dimension',
	PlayerInVehicle = 'player_in_vehicle',
	PlayerDead = 'player_dead',

	FarFromJobMarker = 'far_from_job_marker',
	FarFromPlants = 'far_from_plants',
	FarFromStorage = 'far_from_storage',
	FarmerInVehicle = 'farmer_in_vehicle',

	VehicleNumberPlateTaken = 'vehicle_number_plate_taken',
	VehicleNumberPlateRegionNotFound = 'vehicle_number_plate_region_not_found',
	VehicleNumberPlateNotFound = 'vehicle_number_plate_not_found',
	VehicleNumberPlateInvalid = 'vehicle_number_plate_invalid',
	VehicleNumberPlateStorageFull = 'vehicle_number_plate_storage_full',
	VehicleNumberPlateOnModeration = 'vehicle_number_plate_on_moderation',

	CharacterNotFound = 'character_not_found',
	CharacterNameTaken = 'character_name_taken',
	CharacterNameChangePending = 'character_name_change_pending',

	FarFromVehicle = 'far_from_vehicle',
	NotInVehicle = 'not_in_vehicle',
	NotInDriverSeat = 'not_in_driver_seat',
	NotVehicleOwner = 'not_vehicle_owner',
	VehicleNotLoaded = 'vehicle_not_loaded',
	VehicleNotRegistered = 'vehicle_not_registered',
	VehicleNotRegisteredExchange = 'vehicle_not_registered_exchange',
	NoVehiclesToRegister = 'no_vehicles_to_register',
	VehicleAlreadyRegistered = 'vehicle_already_registered',
	VehicleNotRegisteredPeriorExpired = 'vehicle_not_registered_perior_expired',
	VehicleDamaged = 'vehicle_damaged',
	VehicleTuningUnavailable = 'vehicle_tuning_unavailable',
	HandlingPresetCountLimit = 'handling_preset_count_limit',
	HandlingPresetNotFound = 'handling_preset_not_found',
	VehicleCannotBeDisposed = 'vehicle_cannot_be_disposed',

	LicenseAlreadyOwned = 'license_already_owned',
	LicenseNotFound = 'license_not_found',
	LicenseNotOwned = 'license_not_owned',

	DrivingBikeLicenseNotOwned = 'driving_bike_license_not_owned',
	DrivingCarLicenseNotOwned = 'driving_car_license_not_owned',
	DrivingTruckLicenseNotOwned = 'driving_truck_license_not_owned',
	DrivingBusLicenseNotOwned = 'driving_bus_license_not_owned',
	SailingLicenseNotOwned = 'sailing_license_not_owned',
	FlyingLicenseNotOwned = 'flying_license_not_owned',

	HouseNotFound = 'house_not_found',
	HouseMaxCount = 'house_max_count',
	HouseMaxCountOtherPlayer = 'house_max_count_other_player',
	HouseAlreadyOwned = 'house_already_owned',
	HouseNotOwned = 'house_not_owned',
	HousePaidMaxDays = 'house_paid_max_days',
	HouseLocked = 'house_locked',
	OtherCharacterNotEnoughMoney = 'other_character_not_enough_money',

	RateLimitExceeded = 'rate_limit_exceeded',
	WalletNotAllowed = 'wallet_not_allowed',

	PlayerTeleporting = 'player_teleporting',

	ExchangeItemNotEnough = 'exchange_item_not_enough',
	ExchangeItemAlreadyPlaced = 'exchange_item_already_placed',
	ExchangeItemNotPlaced = 'exchange_item_not_placed',
	ExchangeNotFound = 'exchange_not_found',
	ExchangeIsNotInRadius = 'exchange_is_not_in_radius',
	ExchangePlayerAlreadyIn = 'exchange_player_already_in',
	ExchangeAlreadyConfirmed = 'exchange_already_confirmed',
	ExchangeConfirmationDelay = 'exchange_confirmation_delay',
	ExchangeNotEnoughLevel = 'exchange_not_enough_level',
	ExchangeOtherPlayerNotEnoughLevel = 'exchange_other_player_not_enough_level',
	ExchangeDisabled = 'exchange_disabled',
	ExchangeMaxPlacedItems = 'exchange_max_placed_items',
	ExchangeMaxPlacedItemsMoneyAmount = 'exchange_max_placed_items_money_amount',
	ExchangeNotEnoughMoneyForTax = 'exchange_not_enough_money_for_tax',
	ExchangeNotEnoughParkingSlots = 'exchange_not_enough_parking_slots',
	ExchangeNoItems = 'exchange_no_items',

	ClothesItemNotFound = 'clothes_item_not_found',
	ClothesItemNotOwned = 'clothes_item_not_owned',
	ClothesItemAlreadyOwned = 'clothes_item_already_owned',

	TattoosItemNotFound = 'tattoos_item_not_found',
	TattoosItemAlreadyOwned = 'tattoos_item_already_owned',

	BarberItemNotFound = 'barber_item_not_found',

	JobsTaxiTargetPointNotFound = 'jobs_taxi_target_point_not_found',
	JobsTaxiFarFromCustomer = 'jobs_taxi_far_from_customer',
	JobsTaxiFarFromDestination = 'jobs_taxi_far_from_destination',
	JobsVehicleRentNotEnoughMoney = 'jobs_vehicle_rent_not_enough_money',
	JobsCourierNoOwnedVehicles = 'jobs_courier_no_owned_vehicles',

	ParkingSlotsMaxCount = 'parking_slots_max_count',
	ParkingSlotsNotEnough = 'parking_slots_not_enough',

	TuningModNotFound = 'tuning_mod_not_found',
	TuningKitNotFound = 'tuning_kit_not_found',
	TuningItemNotOwned = 'tuning_item_not_owned',
	TuningKitNotOwned = 'tuning_kit_not_owned',
	TuningItemUnavailable = 'tuning_item_unavailable',
	TuningKitUnavailable = 'tuning_kit_unavailable',
	TuningWheelSettingsInvalid = 'tuning_wheel_settings_invalid',

	PromoCodeNotFound = 'promo_code_not_found',
	PromoCodeExpired = 'promo_code_expired',
	PromoCodeAlreadyUsed = 'promo_code_already_used',
	PromoCodePlayTimeNotEnough = 'promo_code_play_time_not_enough',

	VehicleSpawnForbiddenInArea = 'vehicle_spawn_forbidden_in_area',
	VehicleAlreadySpawned = 'vehicle_already_spawned',
	VehicleSpawnInVehicle = 'vehicle_spawn_in_vehicle',
	WorkVehicleOutOfRange = 'work_vehicle_out_of_range',

	VehicleOnMarket = 'vehicle_on_market',
	VehicleMarketIsNotInRadius = 'vehicle_market_is_not_in_radius',
	VehicleMarketMaxPlacedVehicles = 'vehicle_market_max_placed_vehicles',
	VehicleMarketNoFreeLocations = 'vehicle_market_no_free_locations',
	VehicleMarketCannotBuyOwnVehicle = 'vehicle_market_cannot_buy_own_vehicle',

	CharacterNameForbiddenSymbols = 'character_name_forbidden_symbols',
	CharacterFirstNameLengthInvalid = 'character_first_name_length_invalid',
	CharacterLastNameLengthInvalid = 'character_last_name_length_invalid',

	TaskNotFound = 'task_not_found',
	QuestNotFound = 'quest_not_found',
	QuestAlreadyStarted = 'quest_already_started',
	QuestIsLocked = 'quest_is_locked',
	QuestAlreadyCompleted = 'quest_already_completed',
	QuestNotReady = 'quest_not_ready',

	MustBeOutside = 'must_be_outside',

	VehicleWearConsumableNotFound = 'vehicle_wear_consumable_not_found',

	InventoryItemNotFound = 'inventory_item_not_found',
	InventoryItemNotTradeable = 'inventory_item_not_tradeable',
	InventoryItemNotTradeableTemp = 'inventory_item_not_tradeable_temp',
	InventoryContainerNotTradeable = 'inventory_container_not_tradeable',
	InventoryItemNotDroppable = 'inventory_item_not_droppable',
	InventoryContainerNotDroppable = 'inventory_container_not_droppable',
	InventoryNotFound = 'inventory_not_found',
	InventoryNotEnoughSpace = 'inventory_not_enough_space',
	InventoryNotEnoughItems = 'inventory_not_enough_items',

	TechTuningItemNotFound = 'tech_tuning_item_not_found',
	TechTuningNotEnoughItems = 'tech_tuning_not_enough_items',
	TechTuningNotEnoughCases = 'tech_tuning_not_enough_cases',
	TechTuningCaseNotFound = 'tech_tuning_case_not_found',
	TechTuningSlotOccupied = 'tech_tuning_slot_occupied',

	CarWashNotNeeded = 'car_wash_not_needed',
	CarWashInterrupted = 'car_wash_interrupted',

	LootBuyersItemNotFound = 'loot_buyers_item_not_found',

	FishingNotEnoughLevel = 'fishing_not_enough_level',
	FishingNotInLocation = 'fishing_not_in_location',
	FishingLocationClosed = 'fishing_location_closed',
	FishingLocationNotFound = 'fishing_location_not_found',
	FishingNotStarted = 'fishing_not_started',
	FishingAlreadyStarted = 'fishing_already_started',

	PlayerJailed = 'player_jailed',

	AdsQueueLimitReached = 'ads_queue_limit_reached',
	AdNotFound = 'ad_not_found',
	AdTextTooLong = 'ad_text_too_long',
	AdDuplicate = 'ad_duplicate',

	CasesLootboxNotFound = 'cases_lootbox_not_found',
	CasesBundleNotFound = 'cases_bundle_not_found',

	DonateItemNotFound = 'donate_item_not_found',
	DonateItemNotAvailable = 'donate_item_not_available',

	DialogNotFound = 'dialog_not_found',
	CannotMessageSelf = 'cannot_message_self',
	PlayerNotOnline = 'player_not_online',

	TuningCustomWheelsRequired = 'tuning_custom_wheels_required',

	CharacterAlreadyOnline = 'character_already_online',
	InventoryFull = 'inventory_full',
	JobCooldown = 'job_cooldown',

	FeedbackTitleTooShort = 'feedback_title_too_short',
	FeedbackTitleTooLong = 'feedback_title_too_long',
	FeedbackMessageTooShort = 'feedback_message_too_short',
	FeedbackMessageTooLong = 'feedback_message_too_long',
	FeedbackCooldownExceeded = 'feedback_cooldown_exceeded',

	PinResetRateLimitExceeded = 'pin_reset_rate_limit_exceeded',
	PinResetCodeInvalid = 'pin_reset_code_invalid',
	PinResetCodeExpired = 'pin_reset_code_expired',
	PinResetTokenInvalid = 'pin_reset_token_invalid',
	PinNotSet = 'pin_not_set',

	PasswordResetRateLimitExceeded = 'password_reset_rate_limit_exceeded',
	PasswordResetCodeInvalid = 'password_reset_code_invalid',
	PasswordResetCodeExpired = 'password_reset_code_expired',
	PasswordResetTokenInvalid = 'password_reset_token_invalid',

	RegistrationCodeInvalid = 'registration_code_invalid',
	RegistrationCodeExpired = 'registration_code_expired',
	RegistrationEmailTaken = 'registration_email_taken',
	RegistrationLoginTaken = 'registration_login_taken',
}

export class NotAuthorizedError extends ClientError {
	constructor() {
		super(ClientErrorCode.NotAuthorized);
	}
}

export class AccessDeniedError extends ClientError {
	constructor() {
		super(ClientErrorCode.AccessDenied);
	}
}

const errorCodeToMessage: Record<ClientErrorCode, string> = {
	[ClientErrorCode.InternalError]: 'Внутренняя ошибка сервера',
	[ClientErrorCode.HousesEditorError]: '{message}',
	[ClientErrorCode.NotAuthorized]: 'Вы не авторизованы',
	[ClientErrorCode.AccountNotFound]: 'Аккаунт не найден',
	[ClientErrorCode.WrongPassword]: 'Неверное имя пользователя или пароль',
	[ClientErrorCode.EmailTaken]: 'Аккаунт уже существует',
	[ClientErrorCode.LoginTaken]: 'Логин уже занят',
	[ClientErrorCode.AlreadyAuthorized]: 'Вы уже авторизованы',
	[ClientErrorCode.WrongServerPassword]: 'Неверный пароль сервера',
	[ClientErrorCode.WrongClosedBetaCode]: 'Неверный код доступа',
	[ClientErrorCode.NotEnoughMoney]: 'Недостаточно средств',
	[ClientErrorCode.VehicleNotFound]: 'Транспортное средство не найдено',
	[ClientErrorCode.VehicleSeatTaken]: 'Указанное место в транспорте уже занято',
	[ClientErrorCode.VehicleDoorsClosed]: 'Двери в ТС закрыты',
	[ClientErrorCode.MaxSpawnedVehiclesReached]: 'Вы достигли максимального количества заспавненных ТС',
	[ClientErrorCode.NumberplateRegionNotFound]: 'Регион номерного знака не найден',
	[ClientErrorCode.AccessDenied]: 'У вас нет доступа к этому действию',
	[ClientErrorCode.FuelTypeNotCompatible]: 'Тип топлива не совместим с вашим двигателем',
	[ClientErrorCode.BankExchangeDirectionNotAllowed]: 'Данный тип перевода не доступен',
	[ClientErrorCode.BankTransferSelf]: 'Нельзя перевести деньги самому себе',
	[ClientErrorCode.PlayerNotFound]: 'Игрок не найден',
	[ClientErrorCode.DataFetchingFailed]: 'Не удалось загрузить данные',
	[ClientErrorCode.PlayerOnJob]: 'Увольтесь с другой работы',
	[ClientErrorCode.PlayerInDimension]: 'Нужно находиться в обычном мире',
	[ClientErrorCode.PlayerInVehicle]: 'Покиньте текущий транспорт',
	[ClientErrorCode.PlayerDead]: 'Вы мертвы',
	[ClientErrorCode.FarFromJobMarker]: 'Вы находитесь слишком далеко от маркера начала работы',

	[ClientErrorCode.FarFromPlants]: 'Вы находитесь слишком далеко от растения',
	[ClientErrorCode.FarFromStorage]: 'Вы находитесь слишком далеко от склада',
	[ClientErrorCode.FarmerInVehicle]: 'Вы не можете собирать растения, находясь в транспорте',

	[ClientErrorCode.VehicleNumberPlateTaken]: 'Номерной знак уже занят',
	[ClientErrorCode.VehicleNumberPlateRegionNotFound]: 'Регион не найден',
	[ClientErrorCode.VehicleNumberPlateNotFound]: 'Номерной знак не найден',
	[ClientErrorCode.VehicleNumberPlateInvalid]: 'Номерной знак имеет недопустимый формат',
	[ClientErrorCode.VehicleNumberPlateStorageFull]: 'Вы достигли максимального количества номерных знаков в хранилище',
	[ClientErrorCode.VehicleNumberPlateOnModeration]: 'Номерной знак находится на модерации',

	[ClientErrorCode.CharacterNotFound]: 'Персонаж не найден',
	[ClientErrorCode.CharacterNameTaken]: 'Персонаж с таким именем и фамилией уже существует',
	[ClientErrorCode.CharacterNameChangePending]: 'У вас уже есть активная заявка на смену имени',

	[ClientErrorCode.FarFromVehicle]: 'Транспорт находится слишком далеко',
	[ClientErrorCode.NotInVehicle]: 'Вы не находитесь в транспорте',
	[ClientErrorCode.NotInDriverSeat]: 'Вы сидите не на водительском месте',
	[ClientErrorCode.NotVehicleOwner]: 'Транспорт вам не принадлежит',
	[ClientErrorCode.VehicleNotLoaded]: 'Ваш транспорт еще не загружен',
	[ClientErrorCode.VehicleNotRegistered]: 'Ваш транспорт не зарегистрирован',
	[ClientErrorCode.VehicleNotRegisteredExchange]: 'Данный транспорт не зарегистрирован',
	[ClientErrorCode.NoVehiclesToRegister]: 'У вас нет незарегистрированного транспорта',
	[ClientErrorCode.VehicleAlreadyRegistered]: 'Выбранный транспорт уже зарегистрирован',
	[ClientErrorCode.VehicleNotRegisteredPeriorExpired]:
		'Для дальнейшего использования транспорта требуется регистрация',
	[ClientErrorCode.VehicleDamaged]: 'Транспорт повреждён',
	[ClientErrorCode.VehicleTuningUnavailable]: 'Тюнинг недоступен во время работы или квеста.',
	[ClientErrorCode.HandlingPresetCountLimit]: 'Вы достигли максимального количества сохраненных настроек хендлинга',
	[ClientErrorCode.HandlingPresetNotFound]: 'Настройка хендлинга не найдена',
	[ClientErrorCode.VehicleCannotBeDisposed]: 'Квестовый и рабочий транспорт нельзя утилизировать',

	[ClientErrorCode.LicenseAlreadyOwned]: 'Вы уже получили эту лицензию',
	[ClientErrorCode.LicenseNotFound]: 'Лицензия не существует',
	[ClientErrorCode.LicenseNotOwned]: 'У вас отсутствует лицензия',

	[ClientErrorCode.DrivingBikeLicenseNotOwned]: 'У вас отсутствует лицензия на управление мототранспортом',
	[ClientErrorCode.DrivingCarLicenseNotOwned]: 'У вас отсутствует лицензия на управление легковыми авто',
	[ClientErrorCode.DrivingTruckLicenseNotOwned]: 'У вас отсутствует лицензия на управление грузовыми авто',
	[ClientErrorCode.DrivingBusLicenseNotOwned]: 'У вас отсутствует лицензия на управление пассажирским транспортом',
	[ClientErrorCode.SailingLicenseNotOwned]: 'У вас отсутствует лицензия на управление водным транспортом',
	[ClientErrorCode.FlyingLicenseNotOwned]: 'У вас отсутствует лицензия на управление летательными аппаратами',

	[ClientErrorCode.HouseNotFound]: 'Дом не найден',
	[ClientErrorCode.HouseMaxCount]: 'Вы достигли максимального количества домов',
	[ClientErrorCode.HouseMaxCountOtherPlayer]: 'Игрок достиг максимального количества домов',
	[ClientErrorCode.HouseAlreadyOwned]: 'Дом уже принадлежит игроку',
	[ClientErrorCode.HouseNotOwned]: 'Дом вам не принадлежит',
	[ClientErrorCode.HousePaidMaxDays]: 'Дом оплачен на максимальное количество дней',
	[ClientErrorCode.HouseLocked]: 'Дом закрыт',
	[ClientErrorCode.OtherCharacterNotEnoughMoney]: 'У игрока недостаточно денег',

	[ClientErrorCode.RateLimitExceeded]: 'Превышен лимит запросов',
	[ClientErrorCode.WalletNotAllowed]: 'Данный способ оплаты не доступен',

	[ClientErrorCode.PlayerTeleporting]: 'Вы уже перемещаетесь',

	[ClientErrorCode.ExchangeItemNotEnough]: 'У вас не хватает небходимого предмета для обмена',
	[ClientErrorCode.ExchangeItemAlreadyPlaced]: 'Предмет уже добавлен в обмен',
	[ClientErrorCode.ExchangeItemNotPlaced]: 'Предмет не найден в обмене',
	[ClientErrorCode.ExchangeNotFound]: 'Обмен не найден',
	[ClientErrorCode.ExchangeIsNotInRadius]: 'Игрок находится слишком далеко для обмена',
	[ClientErrorCode.ExchangePlayerAlreadyIn]: 'Игрок уже находится в обмене',
	[ClientErrorCode.ExchangeAlreadyConfirmed]: 'Обмен уже подтвержден',
	[ClientErrorCode.ExchangeConfirmationDelay]: 'Подтверждение пока не доступно',
	[ClientErrorCode.ExchangeNotEnoughLevel]: 'Недостаточный уровень для обмена',
	[ClientErrorCode.ExchangeOtherPlayerNotEnoughLevel]: 'У второго игрока недостаточный уровень для обмена',
	[ClientErrorCode.ExchangeDisabled]: 'Обмен в данный момент недоступен',
	[ClientErrorCode.ExchangeMaxPlacedItems]: 'В обмен добавлено максимальное количество предметов',
	[ClientErrorCode.ExchangeMaxPlacedItemsMoneyAmount]: 'Общая стоимость предметов в обмене превышает лимит',
	[ClientErrorCode.ExchangeNotEnoughMoneyForTax]: 'У одного из игроков недостаточно денег для оплаты налога',
	[ClientErrorCode.ExchangeNotEnoughParkingSlots]: 'У одного из игроков недостаточно гаражных мест',
	[ClientErrorCode.ExchangeNoItems]: 'В обмен не добавлены предметы',

	[ClientErrorCode.ClothesItemNotFound]: 'Предмет одежды не найден',
	[ClientErrorCode.ClothesItemNotOwned]: 'Предмет одежды не принадлежит вам',
	[ClientErrorCode.ClothesItemAlreadyOwned]: 'Вы уже владеете этим предметом одежды',

	[ClientErrorCode.TattoosItemNotFound]: 'Тату не найдено',
	[ClientErrorCode.TattoosItemAlreadyOwned]: 'Вы уже владеете этим данным тату',

	[ClientErrorCode.BarberItemNotFound]: 'Элемент внешности не найден',

	[ClientErrorCode.JobsTaxiTargetPointNotFound]: 'Не удалось найти следующий заказ',
	[ClientErrorCode.JobsTaxiFarFromCustomer]: 'Заказчик находится слишком далеко от вашей машины',
	[ClientErrorCode.JobsTaxiFarFromDestination]: 'Вы находитесь слишком далеко от конечной точки',
	[ClientErrorCode.JobsVehicleRentNotEnoughMoney]: 'Недостаточно средств для аренты рабочего транспорта',
	[ClientErrorCode.JobsCourierNoOwnedVehicles]: 'Для работы вам необходимо владеть личным транспортом',

	[ClientErrorCode.ParkingSlotsMaxCount]: 'Вы достигли максимального количества гаражных мест',
	[ClientErrorCode.ParkingSlotsNotEnough]: 'У вас недостаточно гаражных мест',

	[ClientErrorCode.TuningModNotFound]: 'Элемент тюнинга не найден',
	[ClientErrorCode.TuningKitNotFound]: 'Набор тюнинга не найден',
	[ClientErrorCode.TuningItemNotOwned]: 'Вы не владеете этим элементом тюнинга',
	[ClientErrorCode.TuningKitNotOwned]: 'Вы не владеете этим набором тюнинга',
	[ClientErrorCode.TuningItemUnavailable]: 'Этот элемент тюнинга недоступен для текущей конфигурации',
	[ClientErrorCode.TuningKitUnavailable]: 'Этот набор тюнинга недоступен для данного транспорта',
	[ClientErrorCode.TuningWheelSettingsInvalid]: 'Недопустимые настройки колёс',

	[ClientErrorCode.PromoCodeNotFound]: 'Промокод не найден',
	[ClientErrorCode.PromoCodeExpired]: 'Промокод просрочен',
	[ClientErrorCode.PromoCodeAlreadyUsed]: 'Промокод уже был использован',
	[ClientErrorCode.PromoCodePlayTimeNotEnough]: 'У вас недостаточно времени в игре',

	[ClientErrorCode.VehicleSpawnForbiddenInArea]: 'В данной зоне запрещен транспорт',
	[ClientErrorCode.VehicleAlreadySpawned]: 'Выбранный транспорт уже заспавнен',
	[ClientErrorCode.VehicleSpawnInVehicle]: 'Транспорт можно заспавнить только пешком',
	[ClientErrorCode.WorkVehicleOutOfRange]:
		'Рабочее транспортное средство слишком далеко от вас. Подойдите ближе и повторите попытку.',

	[ClientErrorCode.VehicleOnMarket]: 'Это ТС находится на продаже',
	[ClientErrorCode.VehicleMarketIsNotInRadius]: 'Игрок находится слишком далеко для взаимодействия',
	[ClientErrorCode.VehicleMarketMaxPlacedVehicles]: 'На рынок выставлено максимальное количество ТС',
	[ClientErrorCode.VehicleMarketNoFreeLocations]: 'Все места для размещения ТС заняты',
	[ClientErrorCode.VehicleMarketCannotBuyOwnVehicle]:
		'Вы не можете приобрести собственное ТС, воспользуйтесь снятием с продажи',

	[ClientErrorCode.CharacterNameForbiddenSymbols]:
		'В имени и/или фамилии были использованы запрещенные символы. Разрешены только латинские буквы',
	[ClientErrorCode.CharacterFirstNameLengthInvalid]:
		'Имя персонажа персонажа слишком короткое/длинное. Минимальная длина - 3 символа, максимальная - 14',
	[ClientErrorCode.CharacterLastNameLengthInvalid]:
		'Фамилия персонажа персонажа слишком короткое/длинное. Минимальная длина - 3 символа, максимальная - 14',

	[ClientErrorCode.TaskNotFound]: 'Задача не найдена',
	[ClientErrorCode.QuestNotFound]: 'Задание не найдено',
	[ClientErrorCode.QuestAlreadyStarted]: 'Задание уже начато',
	[ClientErrorCode.QuestIsLocked]: 'Задание недоступно',
	[ClientErrorCode.QuestAlreadyCompleted]: 'Задание уже выполнено',
	[ClientErrorCode.QuestNotReady]: 'Вы не выполнили задание',

	[ClientErrorCode.MustBeOutside]: 'Вы должны находиться на улице',

	[ClientErrorCode.VehicleWearConsumableNotFound]: 'Расходник не найден',

	[ClientErrorCode.InventoryItemNotFound]: 'Предмет не найден',
	[ClientErrorCode.InventoryItemNotTradeable]: 'Предмет нельзя передавать',
	[ClientErrorCode.InventoryItemNotTradeableTemp]: 'Предмет временно нельзя передавать',
	[ClientErrorCode.InventoryContainerNotTradeable]: 'Хранилище содержит предмет, которые нельзя передавать',
	[ClientErrorCode.InventoryItemNotDroppable]: 'Предмет нельзя выбрасывать',
	[ClientErrorCode.InventoryContainerNotDroppable]: 'Хранилище содержит предмет, который нельзя выбрасывать',
	[ClientErrorCode.InventoryNotFound]: 'Инвентарь не найден',
	[ClientErrorCode.InventoryNotEnoughSpace]: 'Недостаточно места в инвентаре',
	[ClientErrorCode.InventoryNotEnoughItems]: 'Недостаточно предметов',

	[ClientErrorCode.TechTuningItemNotFound]: 'Компонент тюнинга не найден',
	[ClientErrorCode.TechTuningNotEnoughItems]: 'Недостаточно компонентов тюнинга',
	[ClientErrorCode.TechTuningNotEnoughCases]: 'Недостаточно кейсов',
	[ClientErrorCode.TechTuningCaseNotFound]: 'Кейс не найден',
	[ClientErrorCode.TechTuningSlotOccupied]: 'В этот слот уже установлен компонент тюнинга',

	[ClientErrorCode.CarWashNotNeeded]: 'Вашему автомобилю не требуется мойка',
	[ClientErrorCode.CarWashInterrupted]: 'Мойка прервана',

	[ClientErrorCode.LootBuyersItemNotFound]: 'Продаваемый предмет не найден',

	[ClientErrorCode.FishingNotEnoughLevel]: 'Недостаточный уровень для локации',
	[ClientErrorCode.FishingNotInLocation]: 'Вы вне локации для рыбалки',
	[ClientErrorCode.FishingLocationClosed]: 'Локация для рыбалки закрыта',
	[ClientErrorCode.FishingLocationNotFound]: 'Локация для рыбалки не найдена',
	[ClientErrorCode.FishingNotStarted]: 'Рыбалка ещё не начата',
	[ClientErrorCode.FishingAlreadyStarted]: 'Рыбалка уже начата',

	[ClientErrorCode.PlayerJailed]: 'Вы находитесь в тюрьме',

	[ClientErrorCode.AdsQueueLimitReached]: 'Вы достигли максимального количества объявлений в очереди',
	[ClientErrorCode.AdNotFound]: 'Объявление не найдено',
	[ClientErrorCode.AdTextTooLong]: 'Текст объявления слишком длинный',
	[ClientErrorCode.AdDuplicate]: 'Объявление с таким текстом уже существует в очереди',

	[ClientErrorCode.CasesLootboxNotFound]: 'Кейс не найден',
	[ClientErrorCode.CasesBundleNotFound]: 'Набор не найден',

	[ClientErrorCode.DonateItemNotFound]: 'Предмет не найден',
	[ClientErrorCode.DonateItemNotAvailable]: 'Предмет недоступен',

	[ClientErrorCode.DialogNotFound]: 'Диалог не найден',
	[ClientErrorCode.CannotMessageSelf]: 'Нельзя отправить сообщение самому себе',
	[ClientErrorCode.PlayerNotOnline]: 'Игрок не в сети',

	[ClientErrorCode.TuningCustomWheelsRequired]: 'Для изменения профиля шин требуются кастомные диски',

	[ClientErrorCode.CharacterAlreadyOnline]: 'Персонаж уже онлайн на другом сервере',
	[ClientErrorCode.InventoryFull]: 'Инвентарь переполнен',
	[ClientErrorCode.JobCooldown]: 'Подождите перед повторным действием',

	[ClientErrorCode.FeedbackTitleTooShort]: 'Заголовок слишком короткий (мин. {min} символов)',
	[ClientErrorCode.FeedbackTitleTooLong]: 'Заголовок слишком длинный (макс. {max} символов)',
	[ClientErrorCode.FeedbackMessageTooShort]: 'Сообщение слишком короткое (мин. {min} символов)',
	[ClientErrorCode.FeedbackMessageTooLong]: 'Сообщение слишком длинное (макс. {max} символов)',
	[ClientErrorCode.FeedbackCooldownExceeded]: 'Вы уже отправляли обращение недавно. Попробуйте позже',

	[ClientErrorCode.PinResetRateLimitExceeded]: 'Слишком частые запросы на сброс пин-кода. Попробуйте позже',
	[ClientErrorCode.PinResetCodeInvalid]: 'Неверный код подтверждения',
	[ClientErrorCode.PinResetCodeExpired]: 'Срок действия кода истек. Запросите новый код',
	[ClientErrorCode.PinResetTokenInvalid]: 'Сессия сброса пин-кода устарела. Начните заново',
	[ClientErrorCode.PinNotSet]: 'Пин-код ещё не установлен. Сначала установите пин-код в банке',

	[ClientErrorCode.PasswordResetRateLimitExceeded]: 'Слишком частые запросы на сброс пароля. Попробуйте позже',
	[ClientErrorCode.PasswordResetCodeInvalid]: 'Неверный код подтверждения',
	[ClientErrorCode.PasswordResetCodeExpired]: 'Срок действия кода истек. Запросите новый код',
	[ClientErrorCode.PasswordResetTokenInvalid]: 'Сессия сброса пароля устарела. Начните заново',

	[ClientErrorCode.RegistrationCodeInvalid]: 'Неверный код подтверждения',
	[ClientErrorCode.RegistrationCodeExpired]: 'Срок действия кода истек. Запросите новый код',
	[ClientErrorCode.RegistrationEmailTaken]: 'Аккаунт с такой почтой уже существует',
	[ClientErrorCode.RegistrationLoginTaken]: 'Логин уже занят. Вернитесь назад и выберите другой логин',
};
